import { normalize } from 'normalizr';
import { ActionTree } from 'vuex';
import { reply, list } from '@descomplica/dex-store/lib/normalizr_schema';
import { dexQuery } from '@/helpers/graphql';
import { ListState } from './state';
import RootState from '@/store/root_state';
import {
  listBySlug,
  revisionListBySlug,
  getListsProgressQuery,
  listsAnsweredSolutionsQuery,
} from '../../queries/dexQueries';
import { QuestionInfo } from '@descomplica/dex-models';
import { UndergradGradesService } from '@/services/UndergradGrades';

const actions: ActionTree<ListState, RootState> = {
  async PULL_LIST_ITEMS({ commit, dispatch }, payload) {
    const { listSlug, userId } = payload;
    const response = await dexQuery(listBySlug, { listSlug, userId });

    if (!response.data.listBySlug) return;

    const normalizedResponse = normalize(response.data.listBySlug, list);

    if (normalizedResponse.entities.assertions) {
      const assertions = await new UndergradGradesService().getAssertionsCorrections(
        Object.keys(normalizedResponse.entities.assertions).map((key: any) => Number(key)),
      );

      Object.entries(normalizedResponse.entities.assertions).forEach(([key, value]) => {
        const relatedAssertion = assertions.find(({ id }: any) => id === Number(key));

        if (relatedAssertion) value.correct = relatedAssertion.correct;
      });
    }

    commit('SET_ARTICLES', normalizedResponse.entities);
    commit('SET_ASSERTIONS', normalizedResponse.entities);
    commit('SET_CONTENTS', normalizedResponse.entities);
    commit('SET_IMAGES', normalizedResponse.entities);
    commit('SET_LISTS', normalizedResponse.entities);
    commit('SET_LIST_ITEMS', normalizedResponse.entities);
    commit('SET_PROGRESSES', normalizedResponse.entities);
    commit('SET_QUESTIONS', normalizedResponse.entities);
    commit('SET_REPLIES', normalizedResponse.entities);
    commit('SET_COMMENTS', normalizedResponse.entities);
    commit('SET_EXERCISE_COMMENT_ATTACHMENTS', normalizedResponse.entities);
    commit('SET_TEXTS', normalizedResponse.entities);
    commit('SET_VIDEOS', normalizedResponse.entities);
    commit('SET_USERS', normalizedResponse.entities);

    const repliesNormalized = normalize(response.data.listBySlug.repliesByListId, {
      nodes: [reply],
    });
    commit('SET_REPLIES', repliesNormalized.entities);

    const questions: QuestionInfo[] = Object.values(normalizedResponse.entities.questions || {});
    if (questions.some((question) => !!question.answered)) {
      await dispatch('PULL_LIST_SOLUTIONS', { listSlug });
    }
  },
  async PULL_REVISION_LIST_ITEMS({ commit }, payload) {
    const { listSlug } = payload;
    const response = await dexQuery(revisionListBySlug, { listSlug });

    const normalizedResponse = normalize(response.data.listBySlug, list);

    if (normalizedResponse.entities.assertions) {
      const assertions = await new UndergradGradesService().getAssertionsCorrections(
        Object.keys(normalizedResponse.entities.assertions).map((key: any) => Number(key)),
      );

      Object.entries(normalizedResponse.entities.assertions).forEach(([key, value]) => {
        const relatedAssertion = assertions.find(({ id }: any) => id === Number(key));

        if (relatedAssertion) value.correct = relatedAssertion.correct;
      });
    }

    commit('SET_ASSERTIONS', normalizedResponse.entities);
    commit('SET_CONTENTS', normalizedResponse.entities);
    commit('SET_IMAGES', normalizedResponse.entities);
    commit('SET_LISTS', normalizedResponse.entities);
    commit('SET_LIST_ITEMS', normalizedResponse.entities);
    commit('SET_QUESTIONS', normalizedResponse.entities);
    commit('SET_TEXTS', normalizedResponse.entities);
  },
  async PULL_LISTS_PROGRESS({ commit }, { listIds }: { listIds: number[] }) {
    if (!listIds.length) return;

    const response = await dexQuery(getListsProgressQuery, { listIds });
    const listsProgresses = response?.data?.getListsProgress;

    if (!listsProgresses) return;

    commit('SET_LISTS_PROGRESS', listsProgresses);
  },
  async PULL_LIST_SOLUTIONS({ commit }, { listSlug }: { listSlug: string }) {
    const response = await dexQuery(listsAnsweredSolutionsQuery, { listSlug });

    const normalizedResponse = normalize(response.data.listBySlug, list);

    commit('SET_CONTENTS', normalizedResponse.entities);
    commit('SET_IMAGES', normalizedResponse.entities);
    commit('SET_QUESTIONS', normalizedResponse.entities);
    commit('SET_SOLUTIONS', normalizedResponse.entities);
    commit('SET_TEXTS', normalizedResponse.entities);
    commit('SET_USERS', normalizedResponse.entities);
  },
};

export default actions;
